import {
  BsGraphUp,
  BsDownload,
  BsEye,
  BsFillBarChartFill,
} from "react-icons/bs";
import { Button } from "../../components/Form";
import { useEffect, useState, useRef } from "react";
import Modal from "../../components/Modals/Modal";
import { ChartPediatrics } from "../../components/Charts";
import { Pediatrics } from "../../components/Datas";
import { pdf } from "@react-pdf/renderer";
import ChartPdf from "../../pdf/ChartPdf";
import html2canvas from "html2canvas";
import { saveAs } from "file-saver";

const getNumericValue = (value, multiplier = 1) => {
  const num = parseFloat(value);
  return isNaN(num) ? null : num * multiplier;
};

const BASE_CHART_OPTIONS = {
  names: [],
  options: {
    chart: {
      height: 400,
      type: "line",
      // Sin animación: al arrastrar (pan), la gráfica sigue al cursor 1:1. Con animación activada,
      // ApexCharts "anima" la transición y la vista sobrepasa el movimiento del mouse (se siente
      // más rápida / a saltos).
      animations: { enabled: false },
      // Interacción: arrastrar = dibujar un recuadro sobre la zona a ver de cerca (zoom por selección,
      // preciso y suave). Rueda del mouse = zoom in/out sobre el eje X (edad). El pan nativo de
      // ApexCharts se movía más rápido que el mouse y descuadraba el eje al arrastrar lejos, por eso
      // NO es el modo por defecto (queda igual disponible en el botón de la "mano"). Aplica a las 4
      // gráficas porque todas copian esta base (JSON.parse(JSON.stringify(BASE_CHART_OPTIONS))).
      zoom: {
        enabled: true,
        type: "x",
        allowMouseWheelZoom: true,
      },
      toolbar: {
        show: true,
        autoSelected: "pan", // arrastrar con el mouse desplaza la gráfica
        tools: {
          download: false, // ya existe el botón "Descargar PDF del grafico"
          selection: false,
          zoom: false,
          zoomin: true, // botón +
          zoomout: true, // botón −
          pan: true, // botón de "mano" (modo por defecto)
          reset: true, // 🏠 volver a la vista completa 0–228
        },
      },
    },
    colors: ["#fad7a0", "#f9e79f", "#abebc6", "#aed6f1", "#e6b0aa", "#000"],
    dataLabels: { enabled: false },
    stroke: {
      curve: "smooth",
      width: [3, 3, 3, 3, 3, 3],
      dashArray: [0, 0, 0, 0, 0, 0],
    },
    title: { text: "Gráfica de Crecimiento", align: "left" },
    grid: {
      borderColor: "#e7e7e7",
      row: { colors: ["#f3f3f3", "transparent"], opacity: 0.5 },
    },
    markers: {
      size: [0, 0, 0, 0, 0, 0],
      colors: ["#fad7a0", "#f9e79f", "#abebc6", "#aed6f1", "#e6b0aa", "#000"],
    },
    xaxis: { title: { text: "Eje X" }, type: "numeric" },
    yaxis: { title: { text: "Eje Y" } },
    tooltip: {
      enabled: true,
      shared: false,
      followCursor: false,
      custom({ series, seriesIndex, dataPointIndex, w }) {
        const x = w.globals.seriesX[seriesIndex][dataPointIndex];
        const y = series[seriesIndex][dataPointIndex];
        return `
          <div style="padding: 5px 10px;">
            <div><strong>X:</strong> ${x}</div>
            <div><strong>Y:</strong> ${y}</div>
          </div>`;
      },
    },
    legend: {
      position: "top",
      horizontalAlign: "right",
      floating: false,
      offsetY: -25,
      offsetX: -5,
    },
  },
};

/*  Datos OMS REALES. 0-5 años: WHO 2006 (talla/edad, valores en DE de la referencia MINSAL que entregó la doctora). 5-19 años: WHO 2007 (tablas de campo por percentil P3/P15/P50/P85/P97 ≈ ±2/±1 DE). Anclas [mes, cm], se interpola mes a mes.*/
const buildHeightBands = (isBoy) => {
  const A = isBoy
    ? {
        minus2: [
          [0, 46.1],
          [6, 63.3],
          [12, 71.0],
          [18, 76.9],
          [24, 81.7],
          [36, 88.7],
          [48, 94.9],
          [60, 100.7],
          [72, 106.7],
          [84, 111.8],
          [96, 116.6],
          [108, 121.3],
          [120, 125.8],
          [132, 130.5],
          [144, 135.8],
          [156, 142.1],
          [168, 148.7],
          [180, 154.3],
          [192, 158.3],
          [204, 160.8],
          [216, 162.1],
          [228, 162.8],
        ],
        minus1: [
          [0, 48.0],
          [6, 65.5],
          [12, 73.4],
          [18, 79.6],
          [24, 84.8],
          [36, 92.4],
          [48, 99.1],
          [60, 105.3],
          [72, 110.8],
          [84, 116.3],
          [96, 121.4],
          [108, 126.3],
          [120, 131.2],
          [132, 136.1],
          [144, 141.7],
          [156, 148.3],
          [168, 155.2],
          [180, 160.9],
          [192, 164.8],
          [204, 167.2],
          [216, 168.4],
          [228, 169.0],
        ],
        median: [
          [0, 49.9],
          [6, 67.6],
          [12, 75.7],
          [18, 82.3],
          [24, 87.8],
          [36, 96.1],
          [48, 103.3],
          [60, 109.9],
          [72, 116.0],
          [84, 121.7],
          [96, 127.3],
          [108, 132.6],
          [120, 137.8],
          [132, 143.1],
          [144, 149.1],
          [156, 156.0],
          [168, 163.2],
          [180, 169.0],
          [192, 172.9],
          [204, 175.2],
          [216, 176.1],
          [228, 176.5],
        ],
        plus1: [
          [0, 51.8],
          [6, 69.8],
          [12, 78.1],
          [18, 85.0],
          [24, 90.9],
          [36, 99.8],
          [48, 107.5],
          [60, 114.5],
          [72, 121.1],
          [84, 127.2],
          [96, 133.1],
          [108, 138.8],
          [120, 144.4],
          [132, 150.1],
          [144, 156.4],
          [156, 163.7],
          [168, 171.2],
          [180, 177.0],
          [192, 181.0],
          [204, 183.1],
          [216, 183.9],
          [228, 184.1],
        ],
        plus2: [
          [0, 53.7],
          [6, 71.9],
          [12, 80.5],
          [18, 87.7],
          [24, 93.9],
          [36, 103.5],
          [48, 111.7],
          [60, 119.1],
          [72, 125.2],
          [84, 131.7],
          [96, 137.9],
          [108, 143.9],
          [120, 149.8],
          [132, 155.8],
          [144, 162.4],
          [156, 170.0],
          [168, 177.6],
          [180, 183.6],
          [192, 187.5],
          [204, 189.5],
          [216, 190.2],
          [228, 190.3],
        ],
      }
    : {
        minus2: [
          [0, 45.4],
          [6, 61.2],
          [12, 68.9],
          [18, 74.9],
          [24, 80.0],
          [36, 87.4],
          [48, 94.1],
          [60, 99.9],
          [72, 105.5],
          [84, 110.5],
          [96, 115.7],
          [108, 121.0],
          [120, 126.6],
          [132, 132.5],
          [144, 138.4],
          [156, 143.3],
          [168, 146.7],
          [180, 148.7],
          [192, 149.8],
          [204, 150.3],
          [216, 150.6],
          [228, 150.9],
        ],
        minus1: [
          [0, 47.3],
          [6, 63.5],
          [12, 71.4],
          [18, 77.8],
          [24, 83.2],
          [36, 91.2],
          [48, 98.4],
          [60, 104.7],
          [72, 109.8],
          [84, 115.1],
          [96, 120.5],
          [108, 126.2],
          [120, 132.0],
          [132, 138.1],
          [144, 144.1],
          [156, 149.2],
          [168, 152.6],
          [180, 154.5],
          [192, 155.5],
          [204, 155.9],
          [216, 156.2],
          [228, 156.4],
        ],
        median: [
          [0, 49.1],
          [6, 65.7],
          [12, 74.0],
          [18, 80.7],
          [24, 86.4],
          [36, 95.1],
          [48, 102.7],
          [60, 109.4],
          [72, 115.1],
          [84, 120.8],
          [96, 126.6],
          [108, 132.5],
          [120, 138.6],
          [132, 145.0],
          [144, 151.2],
          [156, 156.4],
          [168, 159.8],
          [180, 161.7],
          [192, 162.5],
          [204, 162.9],
          [216, 163.1],
          [228, 163.2],
        ],
        plus1: [
          [0, 51.0],
          [6, 68.0],
          [12, 76.6],
          [18, 83.6],
          [24, 89.6],
          [36, 98.9],
          [48, 107.0],
          [60, 114.2],
          [72, 120.4],
          [84, 126.5],
          [96, 132.6],
          [108, 138.8],
          [120, 145.3],
          [132, 151.9],
          [144, 158.3],
          [156, 163.6],
          [168, 167.0],
          [180, 168.8],
          [192, 169.6],
          [204, 169.8],
          [216, 169.9],
          [228, 169.9],
        ],
        plus2: [
          [0, 52.9],
          [6, 70.3],
          [12, 79.2],
          [18, 86.5],
          [24, 92.9],
          [36, 102.7],
          [48, 111.3],
          [60, 118.9],
          [72, 124.8],
          [84, 131.1],
          [96, 137.5],
          [108, 144.0],
          [120, 150.7],
          [132, 157.5],
          [144, 164.1],
          [156, 169.4],
          [168, 172.8],
          [180, 174.6],
          [192, 175.3],
          [204, 175.4],
          [216, 175.5],
          [228, 175.5],
        ],
      };
  const interp = (anchors, x) => {
    for (let k = 0; k < anchors.length - 1; k++) {
      const [x0, y0] = anchors[k];
      const [x1, y1] = anchors[k + 1];
      if (x >= x0 && x <= x1)
        return +(y0 + ((y1 - y0) * (x - x0)) / (x1 - x0)).toFixed(1);
    }
    return anchors[anchors.length - 1][1];
  };
  const band = (anchors) => {
    const r = [];
    for (let m = 0; m <= 228; m++) r.push([m, interp(anchors, m)]);
    return r;
  };
  return {
    plus2: band(A.plus2),
    plus1: band(A.plus1),
    median: band(A.median),
    minus1: band(A.minus1),
    minus2: band(A.minus2),
  };
};

const StadisticsResults = ({ options_datas }) => {
  const [openModal, setOpenModal] = useState(false);
  const [chartType, setChartType] = useState(null);
  const [modalTitle, setModalTitle] = useState("");
  const [dataShow, setDataShow] = useState(null);
  const [chartOptions, setChartOptions] = useState(BASE_CHART_OPTIONS);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const chartRef = useRef(null);

  const getScatterData = (
    citas = [],
    xField,
    yField,
    xMultiplier = 1,
    yMultiplier = 1,
  ) => {
    return citas
      .map((cita) => {
        // Validación más estricta
        if (!cita || cita[xField] === undefined || cita[yField] === undefined) {
          return null;
        }

        // Normalización de valores
        const xVal = parseFloat(cita[xField]) * xMultiplier;
        const yVal = parseFloat(cita[yField]) * yMultiplier;

        // Validación de rangos
        if (isNaN(xVal) || isNaN(yVal) || xVal < 0 || yVal < 0) {
          return null;
        }

        return [xVal, yVal];
      })
      .filter((point) => point !== null)
      .sort((a, b) => a[0] - b[0]); // Ordenar por eje X
  };

  const handleOpenChart = (type) => {
    setChartType(type);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setChartType(null);
    setModalTitle("");
  };

  useEffect(() => {
    if (!chartType || !options_datas?.citas) return;

    const gender = options_datas.gender;
    const name = options_datas.name;
    const newOptions = JSON.parse(JSON.stringify(BASE_CHART_OPTIONS));

    let referenceData = [];
    let patientPoints = [];

    switch (chartType) {
      case "HeightWeight":
        setModalTitle("IMC");
        newOptions.names = [
          "Sobrepeso",
          "Peso Alto",
          "Ideal",
          "Bajo peso",
          "Desnutrición",
          "Paciente",
        ];
        newOptions.options.xaxis.title.text = "Edad (meses)";
        newOptions.options.yaxis.title.text = "IMC";
        referenceData =
          gender === "Masculino"
            ? Pediatrics.WeightLengthBoys02
            : Pediatrics.WeightLengthGirls02;

        patientPoints = getScatterData(options_datas.citas, "age", "bmi");

        newOptions.options.xaxis.min = 0;
        newOptions.options.xaxis.max = 228;
        newOptions.options.yaxis.min = 0;
        newOptions.options.yaxis.max = 40;
        break;

      case "WeightAge":
        setModalTitle("Peso/Edad");
        referenceData =
          gender === "Masculino"
            ? Pediatrics.WeightMountBoys
            : Pediatrics.WeightMountGirls;

        patientPoints = getScatterData(
          options_datas.citas,
          "age",
          "peso",
          1,
          1,
        );
        newOptions.options.xaxis.title.text = "Edad (meses)";
        newOptions.options.yaxis.title.text = "Peso (kg)";
        newOptions.options.xaxis.min = 0;
        newOptions.options.xaxis.max = 228;
        newOptions.options.yaxis.min = 0;
        newOptions.options.yaxis.max = 110;
        break;

      case "HeightAge":
        setModalTitle("Altura/Edad");
        referenceData =
          gender === "Masculino"
            ? Pediatrics.LengthMountBoys
            : Pediatrics.LengthMountGirls;

        patientPoints = getScatterData(
          options_datas.citas,
          "age",
          "altura",
          1,
          1,
        );
        newOptions.options.xaxis.title.text = "Edad (meses)";
        newOptions.names = [
          "+2 DE",
          "+1 DE",
          "Mediana",
          "-1 DE",
          "-2 DE",
          "El paciente",
        ];
        newOptions.options.yaxis.title.text = "Altura (cm)";
        newOptions.options.xaxis.min = 0;
        newOptions.options.xaxis.max = 228;
        newOptions.options.yaxis.min = 40;
        newOptions.options.yaxis.max = gender === "Masculino" ? 200 : 180;
        newOptions.options.yaxis.tickAmount = gender === "Masculino" ? 16 : 14;
        break;

      case "HeadCircunference":
        setModalTitle("Circunferencia de cabeza/Edad");
        referenceData =
          gender === "Masculino"
            ? Pediatrics.HeadCircunferenceBoys
            : Pediatrics.HeadCircunferenceGirls;

        patientPoints = getScatterData(
          options_datas.citas,
          "age",
          "circunferencia",
        );
        newOptions.options.xaxis.title.text = "Edad (meses)";
        newOptions.options.yaxis.title.text = "Cabeza (cm)";
        newOptions.options.xaxis.min = 0;
        newOptions.options.xaxis.max = 60;
        newOptions.options.yaxis.min = 25;
        newOptions.options.yaxis.max = 55;
        break;

      default:
        return;
    }

    const toXY = (arr = []) => arr.map((y, i) => [i, y]);

    const demoHeight =
      chartType === "HeightAge"
        ? buildHeightBands(gender === "Masculino")
        : null;

    const completeDataShow =
      chartType === "HeightAge"
        ? {
            sd3: demoHeight.plus2,
            sd2: demoHeight.plus1,
            median: demoHeight.median,
            sdneg2: demoHeight.minus1,
            sdneg3: demoHeight.minus2,
            patient: patientPoints,
          }
        : {
            sd3: toXY(referenceData?.sd3),
            sd2: toXY(referenceData?.sd2),
            median: toXY(referenceData?.median),
            sdneg2: toXY(referenceData?.sdneg2),
            sdneg3: toXY(referenceData?.sdneg3),
            patient: patientPoints,
          };

    setDataShow(completeDataShow);
    setChartOptions(newOptions);
  }, [chartType, options_datas]);

  const handleDownloadPDF = async () => {
    if (!chartRef.current) return;

    setIsGeneratingPdf(true);
    try {
      // 1. Generar imagen del gráfico
      const canvas = await html2canvas(chartRef.current, {
        scale: 2,
        logging: false,
        useCORS: true,
      });
      const imageData = canvas.toDataURL("image/png");

      // 2. Crear el PDF con la imagen generada
      const pdfDoc = (
        <ChartPdf
          chartTitle={modalTitle}
          patientData={options_datas}
          chartImage={imageData}
        />
      );

      // 3. Forzar la descarga del PDF
      const blob = await pdf(pdfDoc).toBlob();
      saveAs(blob, `Grafico_${modalTitle}.pdf`);
    } catch (error) {
      console.error("Error al generar el PDF:", error);
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  return (
    <div className="grid grid-cols-1 gap-5">
      {openModal && (
        <Modal
          closeModal={handleCloseModal}
          isOpen={openModal}
          title={`Comparación de ${modalTitle}`}
          size="xl"
        >
          <div ref={chartRef}>
            {dataShow && chartOptions && (
              <ChartPediatrics datas={dataShow} options={chartOptions} />
            )}
          </div>

          <div className="flex justify-end mt-4">
            {isGeneratingPdf ? (
              <Button disabled>
                <span className="animate-pulse">Generando PDF...</span>
              </Button>
            ) : (
              <Button
                label="Descargar PDF del grafico"
                Icon={BsDownload}
                onClick={handleDownloadPDF}
                className="flex items-center gap-2 bg-blue-500 hover:bg-blue-600 text-white"
              >
                Descargar PDF
              </Button>
            )}
          </div>
        </Modal>
      )}

      {[
        { label: "IMC", type: "HeightWeight" },
        { label: "Peso/Edad", type: "WeightAge" },
        { label: "Altura/Edad", type: "HeightAge" },
        { label: "Circunferencia de cabeza/Edad", type: "HeadCircunference" },
      ].map(({ label, type }) => (
        <div
          key={type}
          className="grid grid-cols-6 items-center text-sm leading-none mb-1"
        >
          <div className="col-span-5">
            <p className="text-xs font-medium">{label}</p>
          </div>
          <div className="col-span-1 flex justify-end">
            <button
              onClick={() => handleOpenChart(type)}
              className=" hover:bg-yellow-400 hover:text-white text-yellow-400  p-[2px] rounded-md mr-2 text-md"
              style={{ width: "35px", height: "22px" }} // Ajusta tamaño del botón
            >
              <BsFillBarChartFill size={15} /> {/* Tamaño de ícono */}
            </button>
          </div>
        </div>
      ))}
    </div>
  );
};

export default StadisticsResults;
